import type { Role } from './roles';

// Every permission names the roles that hold it by default. Admins can grant or
// revoke any permission per user (permission_overrides), which wins over the default.
type PermissionDef = { label: string; group: string; roles: readonly Role[] };

const ALL: readonly Role[] = ['store', 'viewer', 'manager', 'regional_manager', 'template_manager', 'admin'];
const FIELD: readonly Role[] = ['manager', 'regional_manager', 'admin'];

export const PERMISSIONS = {
  // Audits
  view_audits: { label: 'View audits and history', group: 'Audits', roles: ALL },
  start_practice_audit: { label: 'Start practice audits', group: 'Audits', roles: ['store', 'manager', 'regional_manager', 'admin'] },
  start_official_audit: { label: 'Start official audits', group: 'Audits', roles: FIELD },
  unlock_audit: { label: 'Unlock completed audits', group: 'Audits', roles: ['regional_manager', 'admin'] },
  archive_audit: { label: 'Archive audits', group: 'Audits', roles: ['regional_manager', 'admin'] },
  delete_audit: { label: 'Delete audits', group: 'Audits', roles: ['admin'] },
  print_audit: { label: 'Print audit summaries', group: 'Audits', roles: ALL },
  schedule_audits: { label: 'Schedule audits', group: 'Audits', roles: FIELD },

  // Templates
  view_templates: { label: 'View templates', group: 'Templates', roles: ['manager', 'regional_manager', 'template_manager', 'admin'] },
  manage_templates: { label: 'Create, edit and publish templates', group: 'Templates', roles: ['template_manager', 'admin'] },
  delete_templates: { label: 'Delete templates', group: 'Templates', roles: ['admin'] },

  // Action items
  view_action_items: { label: 'View action items', group: 'Action items', roles: ALL },
  create_action_items: { label: 'Create and assign action items', group: 'Action items', roles: FIELD },
  work_action_items: { label: 'Work action items and submit proof', group: 'Action items', roles: ['store', 'manager', 'regional_manager', 'admin'] },
  verify_action_items: { label: 'Verify or reject action items', group: 'Action items', roles: FIELD },

  // Stores
  view_stores: { label: 'View stores', group: 'Stores', roles: ALL },
  edit_stores: { label: 'Edit store settings', group: 'Stores', roles: ['regional_manager', 'admin'] },
  create_stores: { label: 'Create and deactivate stores', group: 'Stores', roles: ['admin'] },
  manage_workers: { label: 'Manage store workers and point assignments', group: 'Stores', roles: FIELD },
  manage_goals: { label: 'Set store goals', group: 'Stores', roles: ['regional_manager', 'admin'] },

  // Dashboards
  view_dashboards: { label: 'View dashboards and comparisons', group: 'Dashboards', roles: ['viewer', 'manager', 'regional_manager', 'template_manager', 'admin'] },

  // Administration
  manage_users: { label: 'Manage users', group: 'Administration', roles: ['admin'] },
  view_activity_log: { label: 'View activity log', group: 'Administration', roles: ['admin'] },
} as const satisfies Record<string, PermissionDef>;

export type Permission = keyof typeof PERMISSIONS;
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as Permission[];

export type PermissionOverrides = Partial<Record<Permission, boolean>>;

export function roleHasPermission(role: Role, perm: Permission): boolean {
  return (PERMISSIONS[perm].roles as readonly Role[]).includes(role);
}

export function hasPermission(
  user: { role: Role; overrides?: PermissionOverrides | null },
  perm: Permission,
): boolean {
  const o = user.overrides?.[perm];
  if (typeof o === 'boolean') return o;
  return roleHasPermission(user.role, perm);
}

export function effectivePermissions(user: { role: Role; overrides?: PermissionOverrides | null }): Permission[] {
  return PERMISSION_KEYS.filter((p) => hasPermission(user, p));
}
